import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { allenamentiTaxonomyEndpoints } from '../../../endpoints/allenamentiTaxonomy';
import { ITrainingCategory } from '../../../models/allenamenti';
import { useDirectorId } from '../../../hooks/directorId';

/**
 * Tassonomia delle categorie di lavoro.
 *
 * Cambia raramente: dieci minuti di cache evitano di richiederla ad ogni
 * apertura della modal.
 *
 * La chiave di cache contiene directorId: senza, un utente con più direttori
 * che cambia entità senza reload vedrebbe le categorie (e gli override) del
 * direttore precedente per tutta la staleTime.
 *
 * `isError`/`error`/`refetch` sono esposti perché se questa GET fallisce la
 * modal di creazione/modifica blocco si ritroverebbe un select Categoria
 * vuoto senza spiegazione: chi consuma l'hook deve poter mostrare l'errore,
 * bloccare il salvataggio e offrire un retry.
 */
export const useAllenamentiTaxonomy = () => {
  const directorId = useDirectorId();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['allenamenti-taxonomy', directorId],
    queryFn: async () => (await allenamentiTaxonomyEndpoints.list()).data,
    staleTime: 10 * 60 * 1000,
    enabled: !!directorId,
  });

  const categories: ITrainingCategory[] = useMemo(() => data ?? [], [data]);

  /** slug → categoria, per risolvere colore ed etichetta di un blocco. */
  const bySlug = useMemo(() => {
    const map = new Map<string, { labelIt: string; color: string }>();
    for (const cat of categories) {
      map.set(cat.slug, { labelIt: cat.labelIt, color: cat.color });
      for (const sub of cat.subcategories) {
        map.set(sub.slug, { labelIt: sub.labelIt, color: sub.color });
      }
    }
    return map;
  }, [categories]);

  return { categories, bySlug, isLoading, isError, error, refetch };
};
